import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from "./Sidebar";
import Header from "./Header";
import { Outlet } from "react-router-dom";
import axios from 'axios';
import Swal from "sweetalert2";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faTractor, faWater, faWifi, faBan, faBolt, faPlug} from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from "react-i18next";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// Import your GeoJSON file directly
import bangladeshDistrictsGeoJSON from './bangladesh-districts.json';

// Set your Mapbox access token here
mapboxgl.accessToken = 'pk.eyJ1IjoiYXF1YWxpbmtiZGx0ZCIsImEiOiJjbWRjcWZ1YTkwMDJhMmxzNms5ZWZhN3dpIn0.snIdCcpoT89QLAo0poMHSA'; // Your actual Mapbox Public Access Token

const Dashboard = (notificationReceivedAt) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [showCards, setShowCards] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [farmMapData, setFarmMapData] = useState(null); // Data for district farm counts
    const location = useLocation();
    const { t } = useTranslation();
    const mapContainer = useRef(null);
    const map = useRef(null);
    // Increased maxWidth for the popup itself to accommodate more content
    const popup = useRef(new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        maxWidth: '500px' // Increased max width for the Mapbox popup container
    }));

    useEffect(() => {
        const storedRoles = JSON.parse(localStorage.getItem('userRoles')) || [];

        if (location.pathname === '/' && (storedRoles.includes('admin') || storedRoles.includes('farmer') || storedRoles.includes('ngo_admin'))) {
            fetchDashboardData();
            fetchFarmMapData();
            setShowCards(true);
            setShowMap(true);
        }
        else if (location.pathname === '/' && storedRoles.includes('super_admin')){
            fetchFarmMapData();
            fetchDashboardData();
            setShowMap(true);
            setShowCards(true);
        }
        else {
            setShowCards(false);
            setShowMap(false);
        }

    }, [location]);

    useEffect(() => {
        if (location.pathname === '/' && showMap && farmMapData) {
            if (map.current) return;

            console.log("Map initialization conditions met. Initializing Mapbox map...");
            console.log("Map container ref:", mapContainer.current);

            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: 'mapbox://styles/mapbox/streets-v11',
                center: [90.3563, 23.685],
                zoom: 6.5
            });

            if (!map.current) {
                console.error("Mapbox map object is null after initialization attempt.");
                return;
            }

            map.current.on('load', () => {
                console.log("Mapbox map loaded.");

                // Create a lookup for farm counts AND the full farms array from the fetched data
                const districtDataLookup = {}; // Will store { "JESSORE": { count: 2, farms: [...] } }
                if (farmMapData && Array.isArray(farmMapData)) {
                    farmMapData.forEach(districtItem => {
                        if (districtItem._id) {
                            const totalFarmsInDistrict = districtItem.farms ? districtItem.farms.length : 0;
                            districtDataLookup[districtItem._id.toUpperCase()] = {
                                count: totalFarmsInDistrict,
                                farms: districtItem.farms || [] // Store the entire farms array
                            };
                        }
                    });
                }

                const processedGeoJSON = { ...bangladeshDistrictsGeoJSON };
                if (processedGeoJSON.features && Array.isArray(processedGeoJSON.features)) {
                    processedGeoJSON.features = processedGeoJSON.features.map(feature => {
                        const districtName = feature.properties.ADM2_EN || feature.properties.NAME_2 || feature.properties.name || feature.properties.DISTRICT || feature.properties.admin2Name;
                        const standardizedDistrictName = districtName ? districtName.toUpperCase() : null;

                        const dataForDistrict = standardizedDistrictName ? districtDataLookup[standardizedDistrictName] : null;

                        return {
                            ...feature,
                            properties: {
                                ...feature.properties,
                                farm_count: dataForDistrict ? dataForDistrict.count : null,
                                farm_data: dataForDistrict ? dataForDistrict.farms : [] // Store the detailed farm data here
                            }
                        };
                    });
                } else {
                    console.error("GeoJSON 'features' array is missing or malformed.", processedGeoJSON);
                    return;
                }

                map.current.addSource('bangladesh-districts', {
                    type: 'geojson',
                    data: processedGeoJSON
                });

                map.current.addLayer({
                    id: 'districts-fill',
                    type: 'fill',
                    source: 'bangladesh-districts',
                    paint: {
                        'fill-color': [
                            'case',
                            ['==', ['get', 'farm_count'], null],
                            '#cccccc',
                            [
                                'interpolate',
                                ['linear'],
                                ['get', 'farm_count'],
                                0, '#646766',
                                1, '#38b888',
                                2, '#12855a',
                                3, '#2b5722',
                                4, '#0b5723',
                                5, '#1d5c31',
                                6, '#1d5c31',
                            ]
                        ],
                        'fill-opacity': 1
                    }
                });

                map.current.addLayer({
                    id: 'districts-borders',
                    type: 'line',
                    source: 'bangladesh-districts',
                    paint: {
                        'line-color': '#ffffff',
                        'line-width': 1
                    }
                });

                map.current.on('click', 'districts-fill', (e) => {
                    if (e.features.length > 0) {
                        const feature = e.features[0];
                        const districtName = feature.properties.ADM2_EN || feature.properties.NAME_2 || feature.properties.name || feature.properties.DISTRICT || feature.properties.admin2Name || 'N/A';
                        const farmCount = feature.properties.farm_count??0;
                        const farmData = JSON.parse(feature.properties.farm_data || '[]'); // Parse the detailed farm data


                        const displayFarmCount = farmCount === null ? 'No Farms Data' : farmCount;

                        let farmsListHtml = '';
                        if (Array.isArray(farmData) && farmData.length > 0) {
                            farmsListHtml = `
                                <h4 style="
                                    margin-top: 15px;
                                    margin-bottom: 8px;
                                    color: #4a4a4a;
                                    font-size: 0.9em;
                                    font-weight: 600;
                                    text-transform: uppercase;
                                ">Farms in ${districtName}:</h4>
                                <ul style="
                                    list-style-type: none;
                                    padding: 0;
                                    margin: 0;
                                    max-height: 200px; /* Increased max height for the overall farms list */
                                    overflow-y: auto;
                                    border: 1px solid #e9ecef;
                                    border-radius: 4px;
                                    background-color: #f8f9fa;
                                    padding: 5px 10px;
                                ">
                                    ${farmData.map(farm => {
                                let pondsHtml = '';
                                if (Array.isArray(farm.ponds) && farm.ponds.length > 0) {
                                    pondsHtml = `
                                                <ul style="list-style-type: none; padding: 0; margin: 5px 0 0 15px; border-left: 2px solid #ddd;">
                                                    ${farm.ponds.map(pond => {
                                        let sensorsHtml = '';
                                        if (Array.isArray(pond.sensors) && pond.sensors.length > 0) {
                                            sensorsHtml = `
    <div style="
        background-color: #e8f5fd; /* A slightly richer light blue */
        border: 1px solid #cce7fa; /* Subtle border */
        border-radius: 6px; /* Slightly more rounded corners */
        padding: 10px 15px; /* Increased padding */
        margin-top: 8px; /* More space from previous content */
        font-size: 0.85em; /* Slightly larger base font size */
        color: #2c3e50; /* Darker text for better contrast */
        max-height: 100px; /* Increased max height for more sensor data */
        overflow-y: auto;
        box-shadow: inset 0 1px 3px rgba(0,0,0,0.05); /* Inner shadow for depth */
    ">
        <p style="
            font-weight: bold;
            margin-bottom: 8px; /* More space below header */
            color: #0056b3; /* Blue for the header */
            border-bottom: 1px dashed #b3d9ff; /* Dashed line separator */
            padding-bottom: 5px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        ">
            <span>Sensor Readings</span>
            <span style="font-weight: normal; font-size: 0.9em; color: #666;">(${pond.last_updated_human})</span>
        </p>
        <div style="margin-top: 5px;">
        ${pond.sensors.map(sensor => `
            <div style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 4px 0;
                border-bottom: 1px dotted #e0e0e0; /* Dotted line for individual sensors */
            ">
                <span style="font-weight: 600; color: #4a4a4a;">${sensor.sensor_name}:</span>
                <span style="font-weight: bold; color: ${sensor.sensor_problem ? '#dc3545' : '#007bff'};">
                    ${sensor.value} ${sensor.unit || ''}
                    ${sensor.sensor_problem ? `<span style="
                        background-color: #dc3545;
                        color: white;
                        padding: 2px 6px;
                        border-radius: 3px;
                        font-size: 0.7em;
                        margin-left: 5px;
                        font-weight: bold;
                    ">PROBLEM!</span>` : ''}
                </span>
            </div>
        `).join('')}
        </div>
    </div>
`;
                                        } else {
                                            sensorsHtml = '<p style="font-size: 0.8em; color: #777; margin-top: 5px;">No sensor data.</p>';
                                        }
                                        return `
                                                            <li style="padding: 5px 0; border-bottom: 1px dotted #e0e0e0;">
                                                                <strong style="color: #0056b3;">Pond: ${pond.name}</strong>
                                                                ${sensorsHtml}
                                                            </li>
                                                        `;
                                    }).join('')}
                                                </ul>
                                            `;
                                } else {
                                    pondsHtml = '<p style="font-size: 0.85em; color: #777; margin-left: 15px;">No ponds registered for this farm.</p>';
                                }

                                return `
                                            <li style="
                                                padding: 8px 0;
                                                border-bottom: 1px solid #dee2e6;
                                                font-size: 0.9em;
                                                color: #495057;
                                            ">
                                                <strong style="color: #2c3e50;">Farm: ${farm.name}</strong>
                                                ${farm.phone && farm.phone !== 'N/A' ? `<p style="font-size: 0.8em; color: #666; margin: 2px 0;">Phone: ${farm.phone}</p>` : ''}
                                                ${pondsHtml}
                                            </li>
                                        `;
                            }).join('')}
                                </ul>
                                ${farmData.length > 0 ? `<p style="text-align: center; font-size: 0.75em; color: #888; margin-top: 5px;">Scroll for more farms/ponds</p>` : ''}
                            `;
                        } else if (farmCount === 0) {
                            farmsListHtml = '<p style="margin-top: 15px; font-size: 0.9em; color: #777; text-align: center;">No farms registered.</p>';
                        } else {
                            farmsListHtml = '<p style="margin-top: 15px; font-size: 0.9em; color: #777; text-align: center;">No farm data available.</p>';
                        }


                        const description = `
                            <div style="
                                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                                padding: 15px;
                                max-width: 500px; /* Adjusted to be slightly less than popup.maxWidth */
                                background-color: #ffffff;
                                border-radius: 8px;
                                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                                color: #333;
                                overflow: hidden;
                            ">
                                <h3 style="
                                    font-weight: 700;
                                    margin: 0 0 12px 0;
                                    color: #2c3e50;
                                    font-size: 1.3em;
                                    text-align: center;
                                    border-bottom: 3px solid #007bff;
                                    padding-bottom: 8px;
                                    letter-spacing: 0.5px;
                                ">${districtName}</h3>

                                <div style="
                                    display: flex;
                                    justify-content: space-between;
                                    align-items: center;
                                    margin-bottom: 10px;
                                    padding: 5px 0;
                                    border-bottom: 1px dashed #e0e0e0;
                                ">
                                    <p style="
                                        margin: 0;
                                        color: #555;
                                        font-weight: 600;
                                        font-size: 0.95em;
                                    ">Total Farms:</p>
                                    <p style="
                                        margin: 0;
                                        color: #007bff;
                                        font-weight: bold;
                                        font-size: 1.25em;
                                    ">${displayFarmCount}</p>
                                </div>

                                ${farmsListHtml}
                            </div>
                        `;

                        popup.current
                            .setLngLat(e.lngLat)
                            .setHTML(description)
                            .addTo(map.current);
                    }
                });

                map.current.on('click', (e) => {
                    const features = map.current.queryRenderedFeatures(e.point, { layers: ['districts-fill'] });
                    if (!features.length) {
                        popup.current.remove();
                    }
                });

                map.current.on('mouseenter', 'districts-fill', () => {
                    map.current.getCanvas().style.cursor = 'pointer';
                });

                map.current.on('mouseleave', 'districts-fill', () => {
                    map.current.getCanvas().style.cursor = '';
                });

            });

            map.current.on('error', (e) => {
                console.error('Mapbox GL JS Error:', e.error);
                Swal.fire('Map Error', `Mapbox encountered an error: ${e.error.message || 'Unknown error'}`, 'error');
            });

        }
        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [location, showMap, farmMapData]);

    // Effect to handle map resizing when sidebar state changes
    useEffect(() => {
        if (map.current && showMap) {
            // Small delay to allow CSS transitions to complete
            setTimeout(() => {
                map.current.resize();
            }, 300);
        }
    }, [isSidebarOpen, showMap]);

    const fetchDashboardData = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setDashboardData(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch dashboard data:", error);
            await Swal.fire('Error', 'Failed to fetch dashboard data.', 'error');
            setDashboardData(null);
        }
    };

    const fetchFarmMapData = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/dashboard/farm-map`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setFarmMapData(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch farm map data:", error);
            await Swal.fire('Error', 'Failed to fetch farm map data.', 'error');
            setFarmMapData(null);
        }
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    return (
        <div className="flex h-screen">
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div
                className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'ml-64' : 'ml-0'} mt-5`}
            >
                <Header toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} notificationReceivedAt={notificationReceivedAt}/>
                <div className="flex-1 p-6 pt-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50 h-full overflow-auto">
                    {location.pathname === '/' && (showCards || showMap) && (
                        <div className="flex gap-8 h-full">
                            {/* Map Section - Left Side */}
                            {showMap && farmMapData && (
                                <div className="flex-1 flex flex-col bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-6">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
                                        <h2 className="text-2xl font-bold text-gray-800">{t('farm_distribution_map')}</h2>
                                    </div>
                                    <div ref={mapContainer} className="flex-1 rounded-xl border-2 border-gray-100 shadow-inner" />
                                </div>
                            )}

                            {/* Dashboard Cards Section - Right Side */}
                            {showCards && dashboardData && (
                                <div className="w-1/2 flex flex-col h-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-6">
                                    {/* User Info Section */}
                                    <div className="mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                                            <h3 className="text-2xl font-bold text-gray-800">{t('user_info')}</h3>
                                        </div>
                                        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 shadow-md rounded-lg p-3 mt-4 relative overflow-hidden border border-indigo-300/20 backdrop-blur-sm">
                                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-white/10 rounded-full"></div>
                                            
                                            <div className="relative z-10">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                        {dashboardData.userInfo?.name?.charAt(0)?.toUpperCase() || 'U'}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-white font-semibold text-sm capitalize leading-tight truncate">
                                                            {dashboardData.userInfo?.name || 'User'}
                                                        </h3>
                                                        <p className="text-indigo-100 text-xs truncate">{dashboardData.userInfo?.email || 'N/A'}</p>
                                                    </div>
                                                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                                </div>
                                                
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2 text-indigo-100">
                                                        <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                                        </svg>
                                                        <span className="text-xs truncate">{dashboardData.userInfo?.phone || 'N/A'}</span>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-2 text-indigo-100">
                                                        <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                                                        </svg>
                                                        <span className="text-xs truncate">{dashboardData.userInfo?.address || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                                            <h3 className="text-2xl font-bold text-gray-800">{t('farm_overview')}</h3>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                            <span className="text-sm text-gray-600 font-medium">{t('live_data')}</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 auto-rows-min">
                                        <div className="group bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 shadow-xl rounded-lg p-2 flex flex-col items-center justify-center hover:scale-105 hover:shadow-2xl transition-all duration-300 border border-blue-300/20 backdrop-blur-sm min-h-[100px] relative overflow-hidden">
                                            <div className="absolute -top-6 -right-6 w-16 h-16 bg-blue-300/20 rounded-full"></div>
                                            <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-blue-400/15 rounded-full"></div>
                                            <div className="text-white text-2xl mb-1 group-hover:scale-110 transition-transform duration-300">
                                                <FontAwesomeIcon icon={faTractor}/>
                                            </div>
                                            <div className="text-center space-y-1 relative z-10">
                                                <h2 className="text-sm font-medium text-blue-100 uppercase tracking-wider">{t('total_farms')}</h2>
                                                <div className="flex items-center justify-center gap-2">
                                                    <p className="text-xl font-extrabold text-white">{dashboardData.farm_count}</p>
                                                    <div className="w-2 h-2 bg-blue-200 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="group bg-gradient-to-br from-emerald-500 via-green-600 to-green-700 shadow-xl rounded-lg p-2 flex flex-col items-center justify-center hover:scale-105 hover:shadow-2xl transition-all duration-300 border border-green-300/20 backdrop-blur-sm min-h-[100px] relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-20 h-8 bg-green-300/20 rounded-bl-full"></div>
                                            <div className="absolute bottom-0 left-0 w-8 h-8 bg-green-400/25 rounded-tr-full"></div>
                                            <div className="text-white text-2xl mb-1 group-hover:scale-110 transition-transform duration-300">
                                                <FontAwesomeIcon icon={faWater}/>
                                            </div>
                                            <div className="text-center space-y-1 relative z-10">
                                                <h2 className="text-sm font-medium text-green-100 uppercase tracking-wider">{t('total_ponds')}</h2>
                                                <div className="flex items-center justify-center gap-2">
                                                    <p className="text-xl font-extrabold text-white">{dashboardData.pond_count}</p>
                                                    <div className="w-2 h-2 bg-green-200 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="group bg-gradient-to-br from-teal-500 via-cyan-600 to-teal-700 shadow-xl rounded-lg p-2 flex flex-col items-center justify-center hover:scale-105 hover:shadow-2xl transition-all duration-300 border border-teal-300/20 backdrop-blur-sm relative overflow-hidden min-h-[100px]">
                                            <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -mr-10 -mt-10"></div>
                                            <div className="text-white text-2xl mb-1 group-hover:scale-110 transition-transform duration-300 relative z-10">
                                                <FontAwesomeIcon icon={faWifi}/>
                                            </div>
                                            <div className="text-center space-y-1 relative z-10">
                                                <h2 className="text-sm font-medium text-teal-100 uppercase tracking-wider">{t('ponds_online')}</h2>
                                                <p className="text-xl font-extrabold text-white">{dashboardData.pond_online}</p>
                                                <div className="w-8 h-1 bg-white/30 rounded-full mx-auto mt-2"></div>
                                            </div>
                                        </div>

                                        <div className="group bg-gradient-to-br from-red-500 via-rose-600 to-red-700 shadow-xl rounded-lg p-2 flex flex-col items-center justify-center hover:scale-105 hover:shadow-2xl transition-all duration-300 border border-red-300/20 backdrop-blur-sm relative overflow-hidden min-h-[100px]">
                                            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full -ml-8 -mb-8"></div>
                                            <div className="text-white text-2xl mb-1 group-hover:scale-110 transition-transform duration-300 relative z-10">
                                                <FontAwesomeIcon icon={faBan}/>
                                            </div>
                                            <div className="text-center space-y-1 relative z-10">
                                                <h2 className="text-sm font-medium text-red-100 uppercase tracking-wider">{t('ponds_offline')}</h2>
                                                <p className="text-xl font-extrabold text-white">{dashboardData.pond_offline}</p>
                                                <div className="w-8 h-1 bg-white/30 rounded-full mx-auto mt-2"></div>
                                            </div>
                                        </div>

                                        <div className="group bg-gradient-to-br from-purple-500 via-violet-600 to-purple-700 shadow-xl rounded-lg p-2 flex flex-col items-center justify-center hover:scale-105 hover:shadow-2xl transition-all duration-300 border border-purple-300/20 backdrop-blur-sm relative min-h-[100px] overflow-hidden">
                                            <div className="absolute top-1 right-1 w-3 h-3 bg-purple-300/30 rounded-full animate-pulse"></div>
                                            <div className="absolute bottom-2 left-2 w-4 h-4 bg-purple-200/20 rounded-full animate-ping"></div>
                                            <div className="absolute top-2 left-2 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                            <div className="text-white text-2xl mb-1 group-hover:scale-110 transition-transform duration-300">
                                                <FontAwesomeIcon icon={faPlug}/>
                                            </div>
                                            <div className="text-center space-y-1 relative z-10">
                                                <h2 className="text-sm font-medium text-purple-100 uppercase tracking-wider flex items-center justify-center gap-1">
                                                    <span>{t('peripherals_on')}</span>
                                                    <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
                                                </h2>
                                                <div className="flex items-center justify-center gap-2">
                                                    <p className="text-xl font-extrabold text-white">{dashboardData.peripherals.on}</p>
                                                    <span className="text-xs text-purple-200 bg-purple-800/30 px-1.5 py-0.5 rounded-full">ON</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="group bg-gradient-to-br from-slate-500 via-gray-600 to-slate-700 shadow-xl rounded-lg p-2 flex flex-col items-center justify-center hover:scale-105 hover:shadow-2xl transition-all duration-300 border border-slate-300/20 backdrop-blur-sm relative min-h-[100px] overflow-hidden">
                                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-slate-400/15 rounded-full"></div>
                                            <div className="absolute top-2 left-2 w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <div className="text-white text-2xl mb-1 group-hover:scale-110 transition-transform duration-300">
                                                <FontAwesomeIcon icon={faPlug}/>
                                            </div>
                                            <div className="text-center space-y-1 relative z-10">
                                                <h2 className="text-sm font-medium text-slate-200 uppercase tracking-wider flex items-center justify-center gap-1">
                                                    <span>{t('peripherals_off')}</span>
                                                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                                                </h2>
                                                <div className="flex items-center justify-center gap-2">
                                                    <p className="text-xl font-extrabold text-white">{dashboardData.peripherals.off}</p>
                                                    <span className="text-xs text-slate-300 bg-slate-800/40 px-1.5 py-0.5 rounded-full">OFF</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="group bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-600 shadow-xl rounded-lg p-2 flex flex-col items-center justify-center hover:scale-105 hover:shadow-2xl transition-all duration-300 border border-yellow-300/20 backdrop-blur-sm relative overflow-hidden min-h-[100px]">
                                            <div className="absolute -top-4 -right-4 w-12 h-12 bg-white/10 rounded-full"></div>
                                            <div className="absolute top-2 left-2 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                            <div className="text-white text-2xl mb-1 group-hover:scale-110 transition-transform duration-300 relative z-10">
                                                <FontAwesomeIcon icon={faBolt}/>
                                            </div>
                                            <div className="text-center space-y-1 relative z-10">
                                                <h2 className="text-sm font-medium text-yellow-100 uppercase tracking-wider flex items-center justify-center gap-1">
                                                    <span>{t('conductors_on')}</span>
                                                    <div className="w-1 h-1 bg-yellow-300 rounded-full animate-pulse"></div>
                                                </h2>
                                                <div className="flex items-center justify-center gap-2">
                                                    <p className="text-xl font-extrabold text-white">{dashboardData.conductors.on}</p>
                                                    <span className="text-xs text-yellow-200 bg-yellow-700/30 px-1.5 py-0.5 rounded-full">⚡ ACTIVE</span>
                                                </div>
                                                <div className="w-8 h-1 bg-white/30 rounded-full mx-auto mt-2"></div>
                                            </div>
                                        </div>

                                        <div className="group bg-gradient-to-br from-orange-500 via-red-500 to-pink-600 shadow-xl rounded-lg p-2 flex flex-col items-center justify-center hover:scale-105 hover:shadow-2xl transition-all duration-300 border border-orange-300/20 backdrop-blur-sm relative min-h-[100px]">
                                            <div className="absolute top-2 left-2 w-2 h-2 bg-gray-400 rounded-full"></div>
                                            <div className="text-white text-2xl mb-1 group-hover:scale-110 transition-transform duration-300">
                                                <FontAwesomeIcon icon={faBolt}/>
                                            </div>
                                            <div className="text-center space-y-1 relative z-10">
                                                <h2 className="text-sm font-medium text-orange-100 uppercase tracking-wider flex items-center justify-center gap-1">
                                                    <span>{t('conductors_off')}</span>
                                                    <div className="w-1 h-1 bg-orange-400 rounded-full opacity-50"></div>
                                                </h2>
                                                <div className="flex items-center justify-center gap-2">
                                                    <p className="text-xl font-extrabold text-white">{dashboardData.conductors.off}</p>
                                                    <span className="text-xs text-orange-200 bg-orange-800/40 px-1.5 py-0.5 rounded-full">IDLE</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {location.pathname !== '/' && <Outlet/>}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;